import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebase/config';
import { collection, getDocs, addDoc, serverTimestamp, doc, getDoc, query, where } from 'firebase/firestore';
import { appConfig } from '../../config/appConfig';
import NavBar from '../NavBar/NavBar';
import { v4 as uuidv4 } from 'uuid';
import './ReservarViaje.css';

function ReservarViaje() {
  // State declarations
  const [formData, setFormData] = useState({
    origen: '',
    destino: '',
    fecha: '',
    hora: '',
    comentarios: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUsersList, setShowUsersList] = useState(false);
  const [currentUserDepartment, setCurrentUserDepartment] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Define these state variables first so we can reference them in memoized functions
  

  // Memoize the fetch functions with useCallback to avoid dependency cycles
  const fetchUsuarios = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const usuariosCollection = collection(db, 'levapan_usuarios');
      const usuariosSnapshot = await getDocs(usuariosCollection);
      const usuariosList = usuariosSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      // .filter(usuario => usuario.status_cliente === "1"); // Removed status filter
      
      setUsuarios(usuariosList);
      console.log("Cargados todos los usuarios (incluyendo activos e inactivos)");
    } catch (err) {
      console.error("Error al obtener usuarios:", err);
      setError("Ocurrió un error al cargar los usuarios. Intenta nuevamente.");
    } finally {
      setLoadingUsers(false);
    }
  }, [/* No external dependencies */]); // This function only uses React setState functions which don't need to be dependencies

  const fetchUsuariosPorDepartamento = useCallback(async (departamento) => {
    try {
      setLoadingUsers(true);
      setError('');
      
      console.log(`Buscando usuarios del departamento: ${departamento}`);
      
      // Si es admin, cargar todos los usuarios en lugar de filtrar
      if (isAdmin) {
        console.log("Administrador detectado - cargando todos los usuarios");
        fetchUsuarios(); // fetchUsuarios now loads all users
        return;
      }
      
      // Usar una consulta filtrada para obtener solo usuarios del mismo departamento
      const usuariosQuery = query(
        collection(db, 'levapan_usuarios'),
        // where('status_cliente', '==', '1'), // Removed status filter
        where('departamento', '==', departamento)
      );
      
      const usuariosSnapshot = await getDocs(usuariosQuery);
      const usuariosDepartamento = usuariosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsuarios(usuariosDepartamento);
      console.log(`Se encontraron ${usuariosDepartamento.length} usuarios en el departamento ${departamento}`);
      
      if (usuariosDepartamento.length === 0) {
        console.warn(`No se encontraron usuarios en el departamento ${departamento}`);
      }
    } catch (err) {
      console.error("Error al obtener usuarios por departamento:", err);
      setError(`Error al cargar usuarios del departamento ${departamento}`);
      // Intentar cargar todos los usuarios como respaldo
      fetchUsuarios();
    } finally {
      setLoadingUsers(false);
    }
  }, [isAdmin, fetchUsuarios]); // Add the proper dependencies

  // Define loadUserDepartment using useCallback with proper dependencies
  const loadUserDepartment = useCallback(async () => {
    if (!auth.currentUser) return;
    
    try {
      // Primero intentar obtener de 'users'
      const userRef = doc(db, 'levapan_users', auth.currentUser.uid);
      let department = null;
      let userIsAdmin = false;
      
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && (userDoc.data().department || userDoc.data().departamento)) {
        department = userDoc.data().department || userDoc.data().departamento;
        // Check if admin in 'users' collection
        userIsAdmin = userDoc.data().isAdmin === true || 
                      userDoc.data().role === 'admin' ||
                      userDoc.data().department === 'Admin';
      } else {
        // Si no existe en 'users', intentar en 'usuarios'
        const usuarioRef = doc(db, 'levapan_usuarios', auth.currentUser.uid);
        const usuarioDoc = await getDoc(usuarioRef);
        
        if (usuarioDoc.exists()) {
          const userData = usuarioDoc.data();
          
          if (userData.departamento) {
            department = userData.departamento;
          }
          
          // Check if user is admin by any criteria
          userIsAdmin = userData.rol === 'admin' || 
                        userData.isAdmin === true || 
                        userData.admin === true ||
                        userData.departamento === 'Admin';
        }
      }
      
      // Set admin status
      setIsAdmin(userIsAdmin);
      console.log("Usuario es admin:", userIsAdmin);
      
      if (department) {
        setCurrentUserDepartment(department);
        console.log("Departamento cargado:", department);
      }
      
      // Si es admin, cargar TODOS los usuarios
      if (userIsAdmin) {
        console.log("Cargando todos los usuarios (modo administrador)");
        fetchUsuarios();
      } else if (department) {
        // Si no es admin pero tiene departamento, filtrar por departamento
        fetchUsuariosPorDepartamento(department);
      } else {
        // Si no es admin ni tiene departamento, cargar todos como fallback
        console.warn("No se encontró el departamento para el usuario actual");
        fetchUsuarios(); // Fallback
      }
    } catch (error) {
      console.error("Error al cargar el departamento del usuario:", error);
      fetchUsuarios(); // Fallback
    }
  }, [fetchUsuarios, fetchUsuariosPorDepartamento]); // Include only necessary dependencies
  
  // Use loadUserDepartment in useEffect
  useEffect(() => {
    const now = new Date();
    const thirtyMinutesLater = new Date(now.getTime() + 30 * 60000);
    
    // Formatear fecha para el input type="date" (YYYY-MM-DD)
    const formattedDate = thirtyMinutesLater.toISOString().split('T')[0];
    
    // Formatear hora para el input type="time" (HH:MM)
    const hours = thirtyMinutesLater.getHours().toString().padStart(2, '0');
    const minutes = thirtyMinutesLater.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;
    
    setFormData(prev => ({
      ...prev,
      fecha: formattedDate,
      hora: formattedTime
    }));
    
    // Call loadUserDepartment
    loadUserDepartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array because we want this to run only once on mount
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Modificación del método de búsqueda
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Mostrar la lista solo si hay término de búsqueda
    setShowUsersList(value.trim().length > 0);
  };
  
  // Limpiar la búsqueda y ocultar la lista
  const clearSearch = () => {
    setSearchTerm('');
    setShowUsersList(false);
  };
  
  // Seleccionar usuario y ocultar la lista
  const handleSelectUser = (usuario) => {
    // Verificar si el usuario ya está seleccionado
    const isSelected = selectedUsers.some(u => u.id === usuario.id);
    
    if (isSelected) {
      // Si ya está seleccionado, lo removemos
      setSelectedUsers(selectedUsers.filter(u => u.id !== usuario.id));
    } else {
      // Si no está seleccionado, lo añadimos (máximo 4)
      if (selectedUsers.length < 4) {
        setSelectedUsers([...selectedUsers, usuario]);
        // Limpiar búsqueda y ocultar lista después de seleccionar
        clearSearch();
      } else {
        setError("Solo puedes seleccionar hasta 4 usuarios por viaje");
        setTimeout(() => setError(""), 3000);
      }
    }
  };
  
  const removeSelectedUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };
  
  // Función de formato fecha para convertir YYYY-MM-DD a DD-MM-YYYY
  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return '';
    const [year, month, day] = fechaISO.split('-');
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.origen || !formData.destino || !formData.fecha || !formData.hora) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }
    
    try {
      setLoading(true);
      setError('');

      // Generar un identificador único para la reserva
      const reservaUuid = uuidv4();

      // Obtener datos del usuario actual (solicitante) desde Firestore
      let solicitanteData = null;
      let solicitanteNombre = '';
      let solicitanteTelefono = '';
      
      if (auth.currentUser) {
        const userRef = doc(db, 'levapan_usuarios', auth.currentUser.uid);
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            solicitanteNombre = userData.nombre_cliente || auth.currentUser.displayName || '';
            solicitanteTelefono = userData.telefono_cliente || '';
            
            // Store complete solicitante data object if needed
            solicitanteData = {
              id: auth.currentUser.uid,
              nombre: solicitanteNombre,
              telefono: solicitanteTelefono,
              cc_cliente: userData.cc_depart || userData.cecos || userData.CC || '',
              cecos: userData.cecos || userData.cc_depart || userData.CC || '',
              cedula: userData.cedula || '',
              departamento: userData.departamento || '',
              localidad: userData.localidad || userData.direccion_cliente || '',
              direccion: userData.localidad || userData.direccion_cliente || '',
              division: userData.division || userData.Division || '',
              code_direccion: userData.code_direccion || '',
              email: auth.currentUser.email || '',
            };
          } else {
            console.warn("Documento del usuario solicitante no encontrado en Firestore.");
            solicitanteNombre = auth.currentUser.displayName || '';
            solicitanteTelefono = '';
          }
        } catch (fetchError) {
          console.error("Error al obtener datos del solicitante:", fetchError);
          solicitanteNombre = auth.currentUser.displayName || '';
          solicitanteTelefono = '';
        }
      }

      // Formatear la fecha del formato YYYY-MM-DD a DD-MM-YYYY
      const fechaFormateada = formatearFecha(formData.fecha);

      // ---> INICIO: Modificar mapeo de pasajeros para incluir TODOS los datos del pasajero
      const pasajeros = selectedUsers.map(user => ({
        id: user.id || '',
        nombre: user.nombre_cliente || '',
        cc_cliente: user.cc_depart || user.cecos || user.CC || '',
        cecos: user.cecos || user.cc_depart || user.CC || '',
        cedula: user.cedula || user.area || user.area || '', // Usar campo cedula directamente
        telefono: user.telefono_cliente || '',
        departamento: user.departamento || 'General',
        localidad: user.localidad || user.direccion_cliente || '',
        direccion: user.localidad || user.direccion_cliente || '',
        code_direccion: user.code_direccion || user.Division || '', // Usar code_direccion directamente o Division como fallback
        status_cliente: user.status_cliente || '1',
        correo: user.correo || user.email || '',
        division: user.division || user.Division || '',
        area: user.area || user.area || '',
        actualizado_en: user.actualizado_en || null
      }));
      // ---> FIN: Modificar mapeo de pasajeros

      // Si no hay pasajeros seleccionados, usar el usuario actual
      if (pasajeros.length === 0 && auth.currentUser) {
        // Obtener datos del usuario actual desde Firestore
        const userRef = doc(db, 'levapan_usuarios', auth.currentUser.uid); // El UID de Auth coincide con el perfil Levapán
        let userData = {};

        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            userData = userDoc.data();
            console.log("Datos del usuario actual:", userData); // Log para depuración
          } else {
             console.warn("Documento del usuario actual no encontrado en Firestore.");
          }
        } catch (fetchError) {
          console.error("Error al obtener datos del usuario actual:", fetchError);
          // Considera si quieres detener la reserva o continuar con datos limitados
        }

        // ---> INICIO: Añadir datos completos para el usuario actual
        pasajeros.push({
          id: auth.currentUser.uid,
          nombre: userData.nombre_cliente || auth.currentUser.displayName || 'Usuario Actual',
          cc_cliente: userData.cc_depart || userData.cecos || userData.CC || '',
          cecos: userData.cecos || userData.cc_depart || userData.CC || '',
          cedula: userData.cedula || userData.area || userData.area || '', // Usar campo cedula directamente
          telefono: userData.telefono_cliente || auth.currentUser.phoneNumber || '',
          departamento: userData.departamento || 'General',
          localidad: userData.localidad || userData.direccion_cliente || '',
          direccion: userData.localidad || userData.direccion_cliente || '',
          code_direccion: userData.code_direccion || userData.Division || '', // Usar code_direccion directamente o Division como fallback
          status_cliente: userData.status_cliente || '1',
          correo: userData.correo || userData.email || auth.currentUser.email || '',
          division: userData.division || userData.Division || '',
          area: userData.area || userData.area || '',
          actualizado_en: userData.actualizado_en || null
        });
        // ---> FIN: Añadir datos completos para el usuario actual
      } else if (pasajeros.length === 0 && !auth.currentUser) {
         setError('Error: No hay usuario autenticado para asignar la reserva.');
         setLoading(false);
         return; // Detener si no hay pasajeros ni usuario actual
      }

      // Crear objeto de reservación con datos del solicitante
      const reservationData = {
        comentarioConductor: formData.comentarios || '',
        createdAt: serverTimestamp(),
        destino: formData.destino.toUpperCase(),
        empresa: appConfig.companyCode,
        fechaHora: fechaFormateada, // Usar DD-MM-YYYY formato
        hora_reserva: formData.hora, // Guardar hora separada si es útil
        fecha_reserva: fechaFormateada, // Guardar fecha en formato DD-MM-YYYY
        fecha: fechaFormateada, // Guardar fecha en formato DD-MM-YYYY
        origen: formData.origen.toUpperCase(),
        pasajeros: pasajeros, // Array con datos completos
        status: 'pending',
        tiempoInicioTaximetro: formData.hora,
        tipo: 'ViejeGrupal',
        userEmail: auth.currentUser ? auth.currentUser.email : '',
        nombreCliente: solicitanteNombre,
        userPhone: solicitanteTelefono,
        userId: auth.currentUser ? auth.currentUser.uid : '',
        uniqueId: reservaUuid, // ID único de la reserva
        
        // Datos del usuario autenticado directo en la raíz del documento
        nombreSolicitante: solicitanteNombre,
        telefonoSolicitante: solicitanteTelefono,
        
        // Agregar datos del primer pasajero en la raíz para filtrado y consulta
        cc: pasajeros.length > 0 ? pasajeros[0].cc_cliente || '' : '',
        cedula: pasajeros.length > 0 ? pasajeros[0].cedula || '' : '',
        code_direccion: pasajeros.length > 0 ? pasajeros[0].code_direccion || '' : '',
        departamento: pasajeros.length > 0 ? pasajeros[0].departamento || '' : '',
        localidad: pasajeros.length > 0 ? pasajeros[0].localidad || pasajeros[0].direccion || '' : '',
        area: pasajeros.length > 0 ? pasajeros[0].area || '' : '',
        division: pasajeros.length > 0 ? pasajeros[0].division || '' : '',
        direccion: pasajeros.length > 0 ? pasajeros[0].direccion || '' : '',
        // Add other passenger fields from the first passenger that might be needed at root level
        
        // También guardar objeto solicitante completo para mayor detalle
        solicitante: solicitanteData
      };

      console.log("Guardando en reservations:", reservationData);
      const reservationsRef = collection(db, 'levapan_reservations');
      const docRef = await addDoc(reservationsRef, reservationData);
      console.log("Reserva guardada en reservations con ID:", docRef.id);

      setSuccess('¡Reserva creada exitosamente!');

      // Limpiar el formulario y selección
      setFormData({
        origen: '',
        destino: '',
        // Mantener fecha/hora o limpiar? Depende de la UX deseada
        fecha: formData.fecha, // Mantener fecha
        hora: formData.hora,   // Mantener hora
        comentarios: ''
      });
      setSelectedUsers([]); // Limpiar usuarios seleccionados
      clearSearch(); // Limpiar búsqueda

    } catch (err) {
      console.error('Error al crear reserva:', err);
      setError('Ocurrió un error al crear la reserva. Intenta nuevamente.');
      setSuccess(''); // Limpiar mensaje de éxito si hubo error
    } finally {
      setLoading(false);
    }
  };
  
  // Filtrar usuarios según término de búsqueda
  const filteredUsers = usuarios.filter(usuario => {
    const term = searchTerm.trim().toLowerCase(); 
    
    if (!term) { 
      // If the search term is empty after trimming (e.g., was empty or only spaces),
      // and the list is somehow visible, show all users.
      // Typically, showUsersList logic (value.trim().length > 0) prevents the list 
      // from being visible with an empty effective search term.
      return true; 
    }
    
    return (
      (usuario.nombre_cliente && usuario.nombre_cliente.toLowerCase().includes(term)) ||
      (usuario.CC && usuario.CC.toLowerCase().includes(term)) || // General CC (badge)
      (usuario.cc_depart && usuario.cc_depart.toLowerCase().includes(term)) || // Departmental CC
      (usuario.telefono_cliente && usuario.telefono_cliente.toLowerCase().includes(term))
    );
  });
  
  return (
    <div className="reservar-container">
      <NavBar />
      <div className="reservar-content">
        <h1 className="page-title">Reservar Viaje</h1>
        
        {success && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i>
            <span>¡Reserva solicitada con éxito! Pronto recibirás confirmación.</span>
          </div>
        )}
        
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}
        
        <div className="reservar-form-container">
          <form className="reservar-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>
                <i className="fas fa-user-friends"></i> Pasajeros
                {isAdmin ? (
                  <span className="departamento-badge admin-badge">
                    <i className="fas fa-crown" style={{marginRight: '5px'}}></i>
                    Acceso Total Administrador
                  </span>
                ) : currentUserDepartment && (
                  <span className="departamento-badge">
                    Departamento: {currentUserDepartment}
                  </span>
                )}
              </h3>
              <p className="section-description">
                {isAdmin
                  ? "Como administrador, puedes seleccionar hasta 4 pasajeros de cualquier departamento"
                  : currentUserDepartment 
                    ? `Selecciona hasta 4 pasajeros de tu departamento (${currentUserDepartment})`
                    : "Selecciona hasta 4 pasajeros para este viaje"}
              </p>
              
              {/* Lista de usuarios seleccionados */}
              <div className="selected-users">
                {selectedUsers.map(user => (
                  <div key={user.id} className="selected-user-chip">
                    <span>{user.nombre_cliente || user.CC || "Usuario"}</span>
                    <button 
                      type="button" 
                      className="remove-user-btn"
                      onClick={() => removeSelectedUser(user.id)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                
                {selectedUsers.length === 0 && (
                  <div className="no-users-selected">
                    <i className="fas fa-user-plus"></i>
                    <span>Ningún usuario seleccionado</span>
                  </div>
                )}
              </div>
              
              {/* Búsqueda de usuarios */}
              <div className="user-search">
                <div className="input-with-icon">
                  <i className="fas fa-search"></i>
                  <input 
                    type="text"
                    placeholder={isAdmin 
                      ? "Buscar usuarios de CUALQUIER DEPARTAMENTO por nombre, CC o teléfono..."
                      : currentUserDepartment 
                        ? `Buscar usuarios de ${currentUserDepartment} por nombre, CC o teléfono...`
                        : "Buscar usuarios por nombre, CC o teléfono..."}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => searchTerm && setShowUsersList(true)}
                  />
                  {searchTerm && (
                    <button 
                      type="button"
                      className="clear-search"
                      onClick={clearSearch}
                    >
                      &times;
                    </button>
                  )}
                </div>
                
                {/* Admin indicator when not searching */}
                {isAdmin && !showUsersList && !searchTerm && (
                  <div className="admin-search-indicator">
                    <i className="fas fa-info-circle"></i>
                    <span>Como administrador, puedes buscar y seleccionar usuarios de todos los departamentos</span>
                  </div>
                )}
                
                {/* Texto de ayuda cuando no hay búsqueda activa */}
                {!isAdmin && !showUsersList && !searchTerm && (
                  <div className="search-help-text">
                    <i className="fas fa-info-circle"></i>
                    <span>Escribe para buscar y seleccionar pasajeros</span>
                  </div>
                )}
              </div>
              
              {/* Lista de usuarios para seleccionar */}
              {showUsersList && (
                <div className="users-list-container">
                  <div className="users-list">
                    {loadingUsers ? (
                      <div className="loading-indicator">
                        <div className="spinner"></div>
                        <span>Cargando usuarios...</span>
                      </div>
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map(usuario => (
                        <div 
                          key={usuario.id} 
                          className={`user-item ${selectedUsers.some(u => u.id === usuario.id) ? 'selected' : ''}`}
                          onClick={() => handleSelectUser(usuario)}
                        >
                          <div className="user-icon">
                            {usuario.nombre_cliente ? 
                              usuario.nombre_cliente.substring(0, 1).toUpperCase() : "U"}
                          </div>
                          <div className="user-info">
                            <div className="user-name">
                              {usuario.nombre_cliente || "Sin nombre"}
                            </div>
                            <div className="user-details">
                              {usuario.CC && 
                                <span className="user-cc">{usuario.CC}</span>}
                              {usuario.telefono_cliente && 
                                <span className="user-phone">{usuario.telefono_cliente}</span>}
                            </div>
                          </div>
                          <div className="user-select-indicator">
                            {selectedUsers.some(u => u.id === usuario.id) && <i className="fas fa-check"></i>}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-users-found">
                        <i className="fas fa-user-slash"></i>
                        <span>{currentUserDepartment 
                          ? `No se encontraron usuarios en el departamento ${currentUserDepartment}`
                          : "No se encontraron usuarios"}
                        </span>
                        <p>Intenta con otra búsqueda</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Botón para cerrar la lista */}
                  <button 
                    type="button"
                    className="close-list-btn"
                    onClick={() => setShowUsersList(false)}
                  >
                    <i className="fas fa-chevron-up"></i> Cerrar
                  </button>
                </div>
              )}
            </div>
            
            <div className="form-section">
              <h3><i className="fas fa-route"></i> Detalles del Viaje</h3>
              
              <div className="form-group">
                <label htmlFor="origen">Punto de partida *</label>
                <div className="input-with-icon">
                  <i className="fas fa-map-marker-alt"></i>
                  <input
                    type="text"
                    id="origen"
                    name="origen"
                    value={formData.origen}
                    onChange={handleChange}
                    placeholder="Ingresa la dirección de origen"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="destino">Punto de destino *</label>
                <div className="input-with-icon">
                  <i className="fas fa-flag-checkered"></i>
                  <input
                    type="text"
                    id="destino"
                    name="destino"
                    value={formData.destino}
                    onChange={handleChange}
                    placeholder="Ingresa la dirección de destino"
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fecha">Fecha *</label>
                  <div className="input-with-icon">
                    <i className="fas fa-calendar-alt"></i>
                    <input
                      type="date"
                      id="fecha"
                      name="fecha"
                      value={formData.fecha}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="hora">Hora *</label>
                  <div className="input-with-icon">
                    <i className="fas fa-clock"></i>
                    <input
                      type="time"
                      id="hora"
                      name="hora"
                      value={formData.hora}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="comentarios">Comentarios al conductor</label>
                <div className="input-with-icon textarea">
                  <i className="fas fa-comment-alt"></i>
                  <textarea
                    id="comentarios"
                    name="comentarios"
                    value={formData.comentarios}
                    onChange={handleChange}
                    placeholder="Información adicional para el conductor..."
                  ></textarea>
                </div>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading || selectedUsers.length === 0}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Procesando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i> Solicitar Taxi para {selectedUsers.length} {selectedUsers.length === 1 ? 'pasajero' : 'pasajeros'}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ReservarViaje;
